import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ListPageTemplate";
import { STATUS_COLORS } from "@shared/schema";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { formatDateTime } from "@/lib/date-utils";
import { PcOrder, getPcOrderStatus } from "@/hooks/use-pc-orders";
import { usePcOrderStatusLabel } from "@/components/pc/pc-order-status";

interface PcOrderCardProps {
  order: PcOrder;
  onView: (order: PcOrder) => void;
  onMarkReady: (order: PcOrder) => void;
  isMarking: boolean;
}

const NOTES_PREVIEW_LENGTH = 90;

function CardRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-right truncate">{value}</span>
    </div>
  );
}

export function PcOrderCard({
  order,
  onView,
  onMarkReady,
  isMarking,
}: PcOrderCardProps) {
  const { t } = useTranslation();
  const formatPcOrderStatus = usePcOrderStatusLabel();
  const [notesExpanded, setNotesExpanded] = useState(false);

  const status = getPcOrderStatus(order);
  const itemCount = Array.isArray(order.products) ? order.products.length : 0;
  const notes = order.notes?.trim();
  const notesOverflow = !!notes && notes.length > NOTES_PREVIEW_LENGTH;

  return (
    <div
      data-testid="pc-order-card"
      className="bg-card p-6 rounded-lg border hover:shadow-md transition-shadow"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-1 gap-3">
            <h3 className="font-semibold text-lg flex-1 min-w-0 truncate">
              {order.title}
            </h3>
            <StatusBadge
              status={status}
              statusColors={STATUS_COLORS}
              formatStatus={formatPcOrderStatus}
            />
          </div>
          <p className="text-base font-medium text-foreground mb-3 truncate">
            {order.contractor.name}
          </p>

          <div className="space-y-2 mb-4">
            <CardRow
              label={t("pages.pcOrders.card.total")}
              value={
                <span className="font-semibold text-lg">
                  ${formatCentsAsDollars(order.totalOrderCost)}
                </span>
              }
            />
            <CardRow label={t("pages.pcOrders.card.items")} value={itemCount} />
            {order.createdByName && (
              <CardRow
                label={t("pages.pcOrders.card.customer")}
                value={order.createdByName}
              />
            )}
            <CardRow
              label={t("pages.pcOrders.card.submitted")}
              value={formatDateTime(order.submittedAt)}
            />
          </div>

          {notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">
                <MessageSquare className="h-3.5 w-3.5" />
                {t("pages.pcOrders.card.notes")}
              </div>
              <p className="text-sm text-amber-900">
                {notesExpanded || !notesOverflow
                  ? notes
                  : `${notes.slice(0, NOTES_PREVIEW_LENGTH)}…`}
              </p>
              {notesOverflow && (
                <button
                  type="button"
                  className="text-sm font-semibold text-amber-700 underline mt-1"
                  onClick={() => setNotesExpanded(!notesExpanded)}
                >
                  {notesExpanded
                    ? t("pages.pcOrders.card.less")
                    : t("pages.pcOrders.card.more")}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {status === "new" && (
            <Button
              className="w-full h-12 text-base"
              size="lg"
              onClick={() => onMarkReady(order)}
              disabled={isMarking}
              data-testid="mark-ready-button"
            >
              {isMarking ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {t("pages.pcOrders.card.markReadyForPickup")}
            </Button>
          )}
          <Button
            className="w-full h-12 text-base"
            size="lg"
            variant="outline"
            onClick={() => onView(order)}
            data-testid="view-pc-order-button"
          >
            {t("pages.pcOrders.card.viewOrder")}
          </Button>
        </div>
      </div>
    </div>
  );
}
